import * as THREE from 'three';
import { COLLAB_CONFIG } from './fiko-collab-types';

/**
 * FikoCollabHighlightManager - Manages remote user selection highlights
 *
 * Shows colored outlines/tints on objects selected by other users
 */
export class FikoCollabHighlightManager {
    private scene: THREE.Scene;

    // Map of userId -> highlighted object UUIDs
    private highlightsByUser: Map<string, Set<string>> = new Map();

    // Map of userId -> highlight color
    private userColors: Map<string, string> = new Map();

    // Map of objectUUID -> highlight mesh
    private highlightMeshes: Map<string, THREE.Mesh> = new Map();

    // Container for highlight meshes
    private highlightContainer: THREE.Group;

    constructor(scene: THREE.Scene) {
        this.scene = scene;

        // Create container for highlights
        this.highlightContainer = new THREE.Group();
        this.highlightContainer.name = 'CollabHighlights';
        this.highlightContainer.renderOrder = 9000;
        this.scene.add(this.highlightContainer);
    }

    /**
     * Apply remote selection highlights
     * Note: 'identifiers' are object names (not UUIDs) for cross-browser sync
     */
    applyRemoteSelection(userId: string, identifiers: string[], color: string): void {
        console.log('[CollabHighlight] applyRemoteSelection from:', userId, 'items:', identifiers?.length);

        // Store user color
        this.userColors.set(userId, color);

        // Get previous highlights for this user
        const previousIds = this.highlightsByUser.get(userId) || new Set<string>();

        // Find objects to unhighlight (were selected, now aren't)
        for (const id of previousIds) {
            if (!identifiers.includes(id)) {
                this.removeHighlight(id, userId);
            }
        }

        // Find objects to highlight (weren't selected, now are)
        for (const id of identifiers) {
            if (!previousIds.has(id)) {
                this.addHighlight(id, userId, color);
            }
        }

        // Update stored highlights
        this.highlightsByUser.set(userId, new Set(identifiers));
    }

    /**
     * Add highlight to an object by name
     * Note: We now use object names (not UUIDs) for cross-browser sync
     */
    private addHighlight(objectName: string, userId: string, color: string): void {
        console.log('[CollabHighlight] Looking for object by name:', objectName);

        // Find the object in scene by name
        let targetObject: THREE.Object3D | undefined;

        this.scene.traverse((obj) => {
            if (obj.name === objectName && !targetObject) {
                targetObject = obj;
            }
        });

        if (!targetObject) {
            console.warn('[CollabHighlight] Object not found by name:', objectName);
            return;
        }

        console.log('[CollabHighlight] Found object:', targetObject.name, targetObject.type);

        // Use name as the key for consistency
        const localKey = objectName;

        // If already highlighted by another user, skip
        if (this.highlightMeshes.has(localKey)) {
            return;
        }

        // Create highlight mesh
        const highlightMesh = this.createHighlightMesh(targetObject, color);
        if (highlightMesh) {
            highlightMesh.userData.collabUserId = userId;
            highlightMesh.userData.collabObjectName = objectName;
            highlightMesh.userData.collabLocalKey = localKey;
            this.highlightContainer.add(highlightMesh);
            this.highlightMeshes.set(localKey, highlightMesh);
            console.log('[CollabHighlight] Highlight added for:', localKey);
        }
    }

    /**
     * Remove highlight from an object by name
     */
    private removeHighlight(objectName: string, userId: string): void {
        const highlight = this.highlightMeshes.get(objectName);
        if (highlight && highlight.userData.collabUserId === userId) {
            this.highlightContainer.remove(highlight);
            this.disposeHighlight(highlight);
            this.highlightMeshes.delete(objectName);
            console.log('[CollabHighlight] Highlight removed for:', objectName);
        }
    }

    /**
     * Create a highlight mesh for an object
     */
    private createHighlightMesh(object: THREE.Object3D, color: string): THREE.Mesh | null {
        // Find mesh in object hierarchy
        let sourceMesh: THREE.Mesh | null = null;

        if (object instanceof THREE.Mesh) {
            sourceMesh = object;
        } else {
            object.traverse((child) => {
                if (child instanceof THREE.Mesh && !sourceMesh) {
                    sourceMesh = child;
                }
            });
        }

        if (!sourceMesh || !sourceMesh.geometry) {
            return null;
        }

        // Create slightly scaled version for outline effect
        const geometry = sourceMesh.geometry.clone();

        const material = new THREE.MeshBasicMaterial({
            color: new THREE.Color(color),
            transparent: true,
            opacity: COLLAB_CONFIG.HIGHLIGHT_OPACITY,
            side: THREE.BackSide,
            depthTest: true,
            depthWrite: false,
        });

        const highlight = new THREE.Mesh(geometry, material);

        // Copy transform from source
        highlight.position.copy(sourceMesh.position);
        highlight.rotation.copy(sourceMesh.rotation);
        highlight.scale.copy(sourceMesh.scale).multiplyScalar(1.02); // Slightly larger

        // Get world matrix
        sourceMesh.updateWorldMatrix(true, false);
        highlight.applyMatrix4(sourceMesh.matrixWorld);

        highlight.renderOrder = 9001;

        return highlight;
    }

    /**
     * Clear highlights for a specific user
     */
    clearUserHighlights(userId: string): void {
        const uuids = this.highlightsByUser.get(userId);
        if (!uuids) return;

        for (const uuid of uuids) {
            this.removeHighlight(uuid, userId);
        }

        this.highlightsByUser.delete(userId);
        this.userColors.delete(userId);
    }

    /**
     * Clear all highlights
     */
    clearAll(): void {
        for (const [userId] of this.highlightsByUser) {
            this.clearUserHighlights(userId);
        }

        // Clean up any remaining meshes
        for (const highlight of this.highlightMeshes.values()) {
            this.highlightContainer.remove(highlight);
            this.disposeHighlight(highlight);
        }
        this.highlightMeshes.clear();
    }

    /**
     * Dispose a highlight mesh
     */
    private disposeHighlight(highlight: THREE.Mesh): void {
        highlight.geometry.dispose();
        if (Array.isArray(highlight.material)) {
            highlight.material.forEach(m => m.dispose());
        } else {
            highlight.material.dispose();
        }
    }

    /**
     * Update highlight positions (call if objects move)
     */
    updateHighlightPositions(): void {
        for (const [uuid, highlight] of this.highlightMeshes) {
            let targetObject: THREE.Object3D | undefined;
            this.scene.traverse((obj) => {
                if (obj.uuid === uuid) {
                    targetObject = obj;
                }
            });

            if (targetObject) {
                targetObject.updateWorldMatrix(true, false);

                // Reset and reapply world matrix
                highlight.matrix.identity();
                highlight.applyMatrix4(targetObject.matrixWorld);
            }
        }
    }

    /**
     * Get all users with highlights
     */
    getHighlightingUsers(): string[] {
        return Array.from(this.highlightsByUser.keys());
    }

    /**
     * Get highlighted objects for a user
     */
    getUserHighlights(userId: string): string[] {
        return Array.from(this.highlightsByUser.get(userId) || []);
    }

    /**
     * Dispose the manager
     */
    dispose(): void {
        this.clearAll();

        if (this.highlightContainer.parent) {
            this.highlightContainer.parent.remove(this.highlightContainer);
        }
    }
}
